import React, { ReactNode, useState } from 'react';
import { useModal } from 'hooks/useModal';
import { IconButton, Stack, Typography } from '@mui/material';
import { APIRequestSort, ModalsKeys, TabsOption } from 'global';
import { DealActivityLinkType } from '@trnsact/trnsact-shared-types';
import { PageContainer } from 'components/shared/Page/PageContainer';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { CardContainer } from 'components/shared/CardContainer/CardContainer';
import { TabsContainer } from 'components/shared/TabsContainer/TabsContainer';
import { ArrowBack, Email, LocalPhone, TextSnippet } from '@mui/icons-material';
import {
  initialFilters,
  ActivityDialog,
  ActivityPageTabs,
  DealActivityList,
  NoteActivityDialog,
  CallActivityDialog,
  ActivityListFilters,
  DealActivityFilters,
  useFetchActivityList,
} from 'modules/dealActivity';

import { TabContainer } from './TabContainer';

const modals: Partial<Record<ModalsKeys, ReactNode>> = {
  [ModalsKeys.ActivityDialog]: <ActivityDialog />,
  [ModalsKeys.CallActivityDialog]: <CallActivityDialog />,
  [ModalsKeys.NoteActivityDialog]: <NoteActivityDialog />,
};

export const DealActivityPage = () => {
  const { parentId } = useParams<{ parentId: string }>();
  const history = useHistory();
  const location = useLocation();

  const params = new URLSearchParams(location.search);
  const parentType = params.get('parentType') as DealActivityLinkType;

  const { handleOpen: onOpenDialog } = useModal(ModalsKeys.ActivityDialog);
  const { handleOpen: onOpenCallDialog } = useModal(ModalsKeys.CallActivityDialog);
  const { handleOpen: onOpenNoteDialog } = useModal(ModalsKeys.NoteActivityDialog);

  const [currentTab, setCurrentTab] = useState<ActivityPageTabs>(ActivityPageTabs.ALL);

  const [sorting, setSorting] = useState<APIRequestSort<'created_date_time'>>({
    key: 'created_date_time',
    value: 'desc',
  });
  const [filters, setFilters] = useState<ActivityListFilters>(initialFilters);

  const { activityList, prevActivityList, loading, error } = useFetchActivityList({
    sorting,
    filters,
    parentId,
    parentType,
    activityType: currentTab,
  });

  const tabs: TabsOption<ActivityPageTabs>[] = [
    {
      label: 'All Activities',
      value: ActivityPageTabs.ALL,
      component: (
        <TabContainer
          actionName="ADD NEW"
          action={() => onOpenDialog({ parentId, parentType })}
          tabTitle={
            <Stack direction="row" alignItems="center" gap="0.5rem">
              <IconButton size="small" onClick={history.goBack}>
                <ArrowBack sx={{ color: 'black' }} />
              </IconButton>

              <Typography variant="h6" component="span">
                All Activity
              </Typography>
            </Stack>
          }
          listSlot={
            <DealActivityList parentId={parentId} list={activityList} loading={loading} parentType={parentType} />
          }
          filterSlot={
            <DealActivityFilters
              type={currentTab}
              loading={loading}
              onFiltersChanged={setFilters}
              onSortingChanged={setSorting}
            />
          }
        />
      ),
    },
    {
      label: 'Email',
      iconStart: <Email />,
      value: ActivityPageTabs.EMAIL,
      component: (
        <TabContainer
          tabTitle="Email"
          listSlot={
            <DealActivityList parentId={parentId} list={activityList} loading={loading} parentType={parentType} />
          }
          filterSlot={
            <DealActivityFilters
              type={currentTab}
              loading={loading}
              onFiltersChanged={setFilters}
              onSortingChanged={setSorting}
            />
          }
        />
      ),
    },
    {
      label: 'Call',
      iconStart: <LocalPhone />,
      value: ActivityPageTabs.CALL,
      component: (
        <TabContainer
          tabTitle="Call"
          actionName="ADD NEW CALL"
          action={() => onOpenCallDialog({ parentId, parentType })}
          listSlot={
            <DealActivityList parentId={parentId} list={activityList} loading={loading} parentType={parentType} />
          }
          filterSlot={
            <DealActivityFilters
              type={currentTab}
              loading={loading}
              onFiltersChanged={setFilters}
              onSortingChanged={setSorting}
            />
          }
        />
      ),
    },
    {
      label: 'Notes',
      iconStart: <TextSnippet />,
      value: ActivityPageTabs.NOTE,
      component: (
        <TabContainer
          tabTitle="Notes"
          action={() => onOpenNoteDialog({ parentId, parentType })}
          actionName="ADD NEW NOTE"
          listSlot={
            <DealActivityList parentId={parentId} list={activityList} loading={loading} parentType={parentType} />
          }
          filterSlot={
            <DealActivityFilters
              type={currentTab}
              loading={loading}
              onFiltersChanged={setFilters}
              onSortingChanged={setSorting}
            />
          }
        />
      ),
    },
  ];

  return (
    <PageContainer title="Deal Activity" modals={modals} error={error} loading={loading && !prevActivityList}>
      <CardContainer>
        <TabsContainer<ActivityPageTabs> tabs={tabs} onTabChange={setCurrentTab} />
      </CardContainer>
    </PageContainer>
  );
};
